import React from "react";
import { Autocomplete, TextField, FormHelperText, Box, AutocompleteProps } from "@mui/material";

interface FormAutocompleteProps extends Partial<Omit<AutocompleteProps<any, any, any, any>, "renderInput">> {
    label: string;
    value: any;
    options: any[];
    onChange: (event: any, value: any) => void;
    onInputChange: (event: any, value: string) => void;
    getOptionLabel: (option: any) => string;
    renderOption?: (props: any, option: any) => React.ReactNode;
    primaryColor?: string;
    placeholder?: string;
    helperText?: string;
    sx?: any;
    freeSolo?: boolean;
}

const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
    label,
    value,
    options,
    onChange,
    onInputChange,
    getOptionLabel,
    renderOption,
    primaryColor = "#0047C0",
    placeholder,
    helperText,
    sx = {},
    freeSolo = true,
    ...rest
}) => {
    return (
        <Box sx={{ width: '100%' }}>
            <Autocomplete
                {...rest}
                freeSolo={freeSolo}
                options={options}
                value={value}
                onChange={onChange}
                onInputChange={onInputChange}
                getOptionLabel={getOptionLabel}
                filterOptions={(x) => x} // Disable built-in filtering to use API results
                slotProps={{
                    ...rest.slotProps,
                    paper: {
                        ...rest.slotProps?.paper,
                        sx: {
                            maxWidth: '90vw',
                            overflowX: 'hidden',
                            maxHeight: 280,
                            ...(rest.slotProps?.paper as any)?.sx,
                        },
                    },
                }}
                renderOption={renderOption || ((props, option, state) => {
                    const { key, ...restProps } = props as any;
                    return (
                        <li
                            key={key || (typeof option === 'string' ? option : option.id || option.label)}
                            {...restProps}
                            style={{
                                ...restProps.style,
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                lineHeight: 1.3,
                                paddingTop: '12px',
                                paddingBottom: '12px',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                borderBottom: "1px solid #e5e5e5",
                            }}
                        >
                            {getOptionLabel(option)}
                        </li>
                    );
                })}
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid #ccc !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: `${primaryColor} !important`,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: `${primaryColor} !important`,
                            borderWidth: '2px !important',
                        },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                        color: `${primaryColor} !important`,
                    },
                    ...sx,
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            type: 'search',
                        }}
                    />
                )}
            />
            {helperText && (
                <FormHelperText sx={{ color: "#f44336", marginLeft: "4px", fontSize: "13px" }}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default FormAutocomplete;
